import { Component, Vue, PropSync, Watch, Emit } from 'vue-property-decorator';
import { ConfigurationField } from '@/models/configuration/Field';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { Helpers } from '@/helpers/Helpers';
import { IConfigurationField } from '@/models/configuration/Field';
import { ApiIdentifers } from '@/config/index';
import { LoggerService } from '@/utils';
import { IFieldEvent } from '@/components/edit/field/Field';

import Field from '@/components/edit/field/Field.vue';
import FieldComponent from '@/components/edit/field/Field';

export interface ISectionFieldsComponentMethods {
    validateSectionFields(options?: { subIndex?: number; checkExtras: boolean }): boolean;
}

@Component({
    components: {
        Field
    },
    name: 'SectionFieldsComponent'
})
export default class SectionFieldsComponent extends Vue implements ISectionFieldsComponentMethods {
    @PropSync('fields', { type: Array }) public syncedFields!: ConfigurationField[];
    @PropSync('sectionRefName', { type: String }) public syncedSectionRefName!: string;
    @PropSync('openAll', { type: Boolean, default: false }) public syncedOpenAll!: boolean;
    @PropSync('previewReadonly', { type: Boolean, default: false }) public previewReadonlyValue!: boolean;

    public subFieldsIndex: number = 0;
    public lastCheckedFieldsIndex: number = 0;
    public openSections: number[] = [0];
    public actionText: string = '';
    public showDelete: boolean = false;
    public showAction: boolean = false;
    public isMandatory: boolean = false;

    public dialog: boolean = false;
    public dialogHeadline: string = '';
    public dialogText: string = '';
    public dialogOk: string = '';

    private normalFieldCount: number = 0;
    private allSectionsOpened = false;

    public mounted() {
        this.updateNormalFieldCount();
        if (this.syncedOpenAll === true && this.allSectionsOpened === false) {
            this.openAllSections(!this.allSectionsOpened);
        }
    }

    public updated() {
        this.updateNormalFieldCount();
    }

    @Watch('syncedFields', { deep: true })
    public onSyncedFieldChanged(newValue: ConfigurationField[]) {
        // LoggerService.LogDebug(`onSyncedFieldChanged (${this.syncedSectionRefName}): `, newValue);
        this.showDelete = this.showAction && this.syncedFields.length > 1;

        if (this.syncedOpenAll === true) {
            this.openAllSections(!this.allSectionsOpened);
        }
    }

    @Emit()
    public fieldEvent(event: IFieldEvent): IFieldEvent {
        return event;
    }

    public hasSubFields(field: ConfigurationField): boolean {
        let hasSubFields = false;
        if (field.type === FieldTypeEnum.SUBFIELD) {
            hasSubFields = true;
        }

        if (field.type === FieldTypeEnum.ACTION_SUBFIELD) {
            hasSubFields = true;
            this.showAction = true;
            this.showDelete = this.showAction && this.syncedFields.length > 1;
            this.actionText = field.action!.title;
            this.isMandatory = field.required;
        }

        return hasSubFields;
    }

    public isSubField(field: ConfigurationField): boolean {
        return field.type === FieldTypeEnum.SUBFIELD;
    }

    public isActionField(field: ConfigurationField): boolean {
        return field.type === FieldTypeEnum.ACTION_SUBFIELD;
    }

    public onAddSubSection() {
        LoggerService.LogDebug(`onAddSubSection clicked`);

        // ✅ Existing validations
        if (this.syncedFields[this.subFieldsIndex].identifier.search(new RegExp(ApiIdentifers.material, 'g')) > -1 && this.checkBlendOnAdd() === false) {
            LoggerService.LogWarn(`validateSection checkBlendOnAdd failed`);
            return;
        }

        if (this.validateSectionFields({ checkExtras: false }) === false) {
            LoggerService.LogWarn(`checkBlendOnAdd failed`);
            return;
        }

        // Clone template row and clear all nested field values
        const clonedSubSecFields: ConfigurationField = new ConfigurationField(this.syncedFields[0]);

        if (clonedSubSecFields.fields) {
            clonedSubSecFields.fields.forEach((field: ConfigurationField) => {
                field.resetFieldValue();
            });
        }

        const baseIdentifier = clonedSubSecFields.identifier.split('__')[0];
        const baseName = clonedSubSecFields.name ? clonedSubSecFields.name.split('__')[0] : baseIdentifier;
        const nextSuffix = this.getNextSubSectionSuffix();

        clonedSubSecFields.position = nextSuffix;
        clonedSubSecFields.identifier = `${baseIdentifier}__${nextSuffix}`;
        clonedSubSecFields.name = `${baseName}__${nextSuffix}`;

        this.syncedFields.push(clonedSubSecFields);
        this.syncedFields = [...this.syncedFields];

        const lastItem = this.syncedFields[this.syncedFields.length - 1];

        if (lastItem.identifier.includes(ApiIdentifers.sample)) {
            const usedExPoints: number[] = [];

            this.syncedFields.forEach(sample => {
                if (sample.fields) {
                    const lab = sample.fields.find(f => f.identifier === ApiIdentifers.sampleLaboratory);

                    if (lab && lab.fields) {
                        const exPoints = lab.fields.find(f => f.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);

                        if (exPoints && exPoints.valueNumber != null) {
                            usedExPoints.push(exPoints.valueNumber);
                        }
                    }
                }
            });

            if (lastItem.fields) {
                const lab = lastItem.fields.find(f => f.identifier === ApiIdentifers.sampleLaboratory);

                if (lab && lab.fields) {
                    const exPoints = lab.fields.find(f => f.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);

                    if (exPoints && exPoints.values) {
                        exPoints.values.forEach(exp => {
                            exp.disabled = usedExPoints.some(x => x === exp.value);
                        });
                    }
                }
            }
        }

        // ✅ Force Vue sync
        this.syncedFields = [...this.syncedFields];

        // ✅ UI updates
        this.subFieldsIndex = this.syncedFields.length - 1;
        this.openAllSections(true);
    }
    private getNextSubSectionSuffix(): number {
        let maxSuffix = -1;
        for (const field of this.syncedFields) {
            const suffix = field.identifier.split('__')[1];
            if (suffix != null) {
                const parsed = parseInt(suffix, 10);
                if (!isNaN(parsed) && parsed > maxSuffix) {
                    maxSuffix = parsed;
                }
            }
        }
        return maxSuffix + 1;
    }

    public deleteIndex: number | null = null;

    public onDeleteSubSection(index: number) {
        LoggerService.LogDebug(`onDeleteSubSection ${index} clicked`);

        // Store index
        this.deleteIndex = index;

        // Set dialog content
        this.dialogHeadline = 'Confirm Delete';
        this.dialogText = 'This action will permanently delete the component.';
        this.dialogOk = 'Confirm';

        // Open dialog
        this.dialog = true;
    }
    public confirmDelete() {
        if (this.deleteIndex === null) {
            return;
        }

        const index = this.deleteIndex;
        const subFieldIndex = index - this.normalFieldCount;

        this.syncedFields.splice(index, 1);
        this.syncedFields = [...this.syncedFields];

        this.openSections = this.openSections
            .filter(i => i !== subFieldIndex)
            .map(i => (i > subFieldIndex ? i - 1 : i));

        if (this.openSections.length === 0 && this.syncedFields.length > 0) {
            const newSubIndex = subFieldIndex > 0 ? subFieldIndex - 1 : 0;
            this.openSections = [newSubIndex];
        }

        if (this.syncedFields.length === 0) {
            this.subFieldsIndex = 0;
        } else if (this.subFieldsIndex >= this.syncedFields.length) {
            this.subFieldsIndex = this.syncedFields.length - 1;
        } else if (index <= this.subFieldsIndex && this.subFieldsIndex > 0) {
            this.subFieldsIndex = index > 0 ? index - 1 : 0;
        }

        this.deleteIndex = null;
        this.dialog = false;
    }

    public getSubSectionDisplayIndex(fieldIndex: number): number {
        return fieldIndex - this.normalFieldCount + 1;
    }

    public cancelDelete() {
        this.deleteIndex = null;
        this.dialog = false;
    }

    public onDialogOk() {
        if (this.deleteIndex !== null) {
            this.confirmDelete();
            return;
        }
        this.dialog = false;
    }
    public splitSubSection(index: number, count: number) {
        const baseField = this.syncedFields[index];
        const base = baseField.identifier.split('__')[0];

        // ✅ remove original
        this.syncedFields.splice(index, 1);

        // ✅ create split items
        for (let i = 0; i < count; i++) {
            const newField = new ConfigurationField(baseField);

            if (newField.fields) {
                newField.fields.forEach(f => f.resetFieldValue());
            }

            this.syncedFields.splice(index + i, 0, newField);
        }

        // ✅ normalize (MOST IMPORTANT)
        this.syncedFields.forEach((f, i) => {
            f.identifier = `${base}__${i}`;
            f.position = i;
        });

        this.syncedFields = [...this.syncedFields];
    }
    /**
     * Method to validate fields
     * @param checkExtras bool
     */
    public validateSectionFields(options?: { subIndex?: number; checkExtras?: boolean; validateOnly?: boolean }): boolean {
        let valid = true;
        const invalidSubSections: number[] = [];
        const checkExtras = options != null && options.checkExtras != null ? options.checkExtras : true;

        if (checkExtras === true) {
            valid = this.checkBlend();
        }

        if (valid === true) {
            let dataFields = this.syncedFields;
            if (options && options.subIndex != null) {
                dataFields = this.syncedFields[options.subIndex].fields || [];
            }

            let validNumber = true;
            for (let index = 0; index < dataFields.length; index++) {
                const field: ConfigurationField = dataFields[index];
                if (field.type === FieldTypeEnum.SUBFIELD || field.type === FieldTypeEnum.ACTION_SUBFIELD) {
                    const subFieldRef = this.getSectionFieldsRef(this.getRefName(field.name, 'sub'));
                    /**
                     * if subFieldRef undefined it may not rendered , fall back to model validation
                     */
                    valid = subFieldRef != null ? subFieldRef.validateSectionFields() : field.isValidField();
                    if (valid === false) {
                        /**
                         * since only fields with subfields create a new expansion-panel
                         * we need to subtract all normal fields from index to get the
                         * correct index for the expansion-panels to open
                         */
                        this.subFieldsIndex = index - this.normalFieldCount;
                        invalidSubSections.push(this.subFieldsIndex);
                        // break;
                    }
                } else {
                    const fieldRef = this.getFieldRef(this.getRefName(field.name, 'field'));

                    if (field.type === FieldTypeEnum.NUMBER && field.unit != null) {
                        const unitField = dataFields.find(f => f.identifier === field.unit);
                        // Fineness value is optional even when Fineness Unit is selected
                        if (
                            field.identifier !== ApiIdentifers.materialFineness &&
                            unitField != null &&
                            unitField.valueNumber != null &&
                            (field.valueNumber == null || isNaN(field.valueNumber))
                        ) {
                            fieldRef.setFieldError(true);
                            valid = false;
                            validNumber = false;
                            // break;
                        }
                    }

                    /**
                     * if fieldRef undefined it may not rendered , fall back to model validation
                     */
                    if ((fieldRef != null ? fieldRef.validateField() : field.isValidField()) === false) {
                        valid = false;
                    }
                }
            }

            if (invalidSubSections.length > 0) {
                if (options && options.validateOnly) {
                    this.openSections = this.openSections;
                } else {
                    this.openSections = invalidSubSections;
                }
                valid = false;
            }

            valid = valid && validNumber;
        }

        return valid;
    }

    public getSetValuesCounter(field: ConfigurationField): number {
        let setValueCount = 0;
        if (field.fields != null) {
            for (const subField of field.fields) {
                setValueCount += subField.getSetValuesCounter();
            }
        } else {
            setValueCount += field.getSetValuesCounter();
        }
        return setValueCount;
    }

    public checkBlend(): boolean {
        let valid = true;

        // check blend on materials
        if (this.syncedFields.some(field => field.identifier.includes(ApiIdentifers.material))) {
            let sum = 0;
            this.syncedFields.forEach((subField: ConfigurationField) => {
                // find Blend% field
                const blend: ConfigurationField | undefined = subField.fields!.find(x => x.identifier === ApiIdentifers.materialBlend);
                if (blend !== undefined) {
                    sum += blend.valueNumber ? blend.valueNumber : 0;
                }
            });
            if (sum !== 100) {
                LoggerService.LogWarn(`Sum of Blend% is not 100`);
                valid = false;

                this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialogBlend.title');
                this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialogBlend.text', sum);
                this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.ok');

                this.dialog = true;
            }
        }
        return valid;
    }

    public checkBlendOnAdd(): boolean {
        let valid = true;

        // check blend on materials
        if (this.syncedFields.some(field => field.identifier.includes(ApiIdentifers.material))) {
            let sum = 0;
            this.syncedFields.forEach((subField: ConfigurationField) => {
                // find Blend% field
                const blend: ConfigurationField | undefined = subField.fields!.find(x => x.identifier === ApiIdentifers.materialBlend);
                if (blend !== undefined) {
                    sum += blend.valueNumber ? blend.valueNumber : 0;
                }
            });
            if (sum >= 100 || sum < 0) {
                LoggerService.LogWarn(`Sum of Blend% is 100%. To add materials it needs to be lower than 100%`);
                valid = false;

                this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialogBlend.title');
                this.dialogText = `${this.$vuetify.lang.t('$vuetify.edit.dialogBlend.text', sum)}<br />${this.$vuetify.lang.t('$vuetify.edit.dialogBlend.textOnAdd')}`;
                this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.ok');

                this.dialog = true;
            }
        }
        return valid;
    }

    public onFieldEvent(event: IFieldEvent) {
        this.fieldEvent(event);
    }

    public onUnitChanged(inField: IConfigurationField) {
        const valueField = this.syncedFields.find((f: IConfigurationField) => f.unit === inField.identifier);
    }

    public onSubFieldClick(subIndex: number) {
        LoggerService.LogDebug(`onSubFieldClick ${subIndex} clicked`);
        const isValid = this.validateSectionFields({ validateOnly: true });
        /**
         * since only fields with subfields create a new expansion-panel
         * we need to subtract all normal fields from index to get the
         * correct index for the expansion-panels to open
         */
        const subFieldIndex = subIndex - this.normalFieldCount;
        const openIndex = this.openSections.findIndex(x => x === subFieldIndex);
        this.lastCheckedFieldsIndex = subIndex;

        /**
         * TODO: check subindex in samples, seems tzo be broken with normalFieldCount
         */

        if (openIndex > -1) {
            const sectionValid = this.validateSectionFields({ subIndex });
            if (sectionValid === true) {
                this.openSections.splice(openIndex, 1);
            }
        } else {
            this.subFieldsIndex = subFieldIndex;
            this.openSections.push(this.subFieldsIndex);
        }
    }

    public getRefName(name: string, prefix: string): string {
        return Helpers.getReferenceName(name, `${this.syncedSectionRefName}-${prefix}`);
    }

    protected openAllSections(openAll: boolean) {
        LoggerService.LogDebug(`openAllSections: ${this.syncedSectionRefName}`);
        let i = 0;
        if (this.syncedFields != null && this.syncedFields.length > 0 && openAll) {
            this.openSections = Array.from(new Array(this.syncedFields.length - this.normalFieldCount), () => i++);
            this.allSectionsOpened = true;
        }
    }

    private getSectionFieldsRef(sectionRefName: string): SectionFieldsComponent {
        return Helpers.getReference<SectionFieldsComponent>(this.$refs, sectionRefName);
    }

    private getFieldRef(sectionRefName: string): FieldComponent {
        return Helpers.getReference<FieldComponent>(this.$refs, sectionRefName);
    }

    private updateNormalFieldCount() {
        // number of fields without subfields
        this.normalFieldCount = this.syncedFields.filter(f => f.type !== FieldTypeEnum.SUBFIELD && f.type !== FieldTypeEnum.ACTION_SUBFIELD).length;
    }
}
